import React from 'react';
import { Call } from '@twilio/voice-sdk';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Tooltip, Typography } from '@material-ui/core';
import clsx from 'clsx';

import ProgressBar from '../../../common/ProgressBar/ProgressBar';
import { didNetworkTestPass } from '../../../../utils';
import { CheckMark } from '../../../../icons/CheckMark';
import { SmallError } from '../../../../icons/SmallError';
import { InfoIcon } from '../../../../icons/InfoIcon';

import { BITRATE_TEST_DURATION } from '../Tests/Tests';
import { getNetworkTestResultDetails } from '../resultRows';
import { NetworkTestName, Edge, TestResults } from '../../../../types';

export enum EdgeResultStatus {
  Pending = 'Ready to Start',
  Running = 'Live Call Test Running',
  Completed = 'Test Completed',
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    border: '1px solid #ddd',
    borderRadius: '3px',
    display: 'flex',
    padding: '0.8em',
    background: '#eee',
    alignItems: 'center',
    margin: '1em 0',
    justifyContent: 'space-between',
  },
  progressContainer: {
    flex: 1,
    padding: '0 1em',
  },
  edgeLabel: {
    minWidth: '170px',
    width: '15%',
    whiteSpace: 'nowrap',
  },
  iconContainer: {
    width: '15%',
    display: 'flex',
    justifyContent: 'flex-end',
    '& svg': {
      margin: '0 0.3em',
    },
  },
  pendingTest: {
    opacity: 0.5,
  },
}));

interface EdgeResultProps {
  codecPreferences: Call.Codec[];
  edge: Edge;
  isActive: boolean;
  result?: TestResults;
  activeTest?: NetworkTestName;
}

const progressBarTimings = {
  'Preflight Test': {
    position: 62.5,
    duration: 25,
  },
  'Bitrate Test': {
    position: 100,
    duration: BITRATE_TEST_DURATION / 1000,
  },
};

export default function EdgeResult(props: EdgeResultProps) {
  const { isActive, result, activeTest } = props;
  const classes = useStyles();

  const progressDuration = activeTest ? progressBarTimings[activeTest].duration : 0;
  const progressPosition = activeTest ? progressBarTimings[activeTest].position : 0;

  const passed = result ? didNetworkTestPass(result) : undefined;

  const sectionTitle = isActive
    ? EdgeResultStatus.Running
    : result
    ? EdgeResultStatus.Completed
    : EdgeResultStatus.Pending;

  return (
    <div className={clsx(classes.container, { [classes.pendingTest]: !isActive && !result })}>
      <Typography className={classes.edgeLabel}>{`${sectionTitle}`}</Typography>
      <div className={classes.progressContainer}>
        {isActive && <ProgressBar position={progressPosition} duration={progressDuration} />}
      </div>
      {result && (
        <div className={classes.iconContainer}>
          {passed ? <CheckMark /> : <SmallError />}
          <Tooltip
            interactive
            placement="top"
            title={
              passed ? (
                <>
                  {getNetworkTestResultDetails(result).map(({ label, value }) => (
                    <Typography key={label} variant="body2">
                      {label}: {value}
                    </Typography>
                  ))}
                </>
              ) : (
                <Typography variant="body2">
                  Test failed
                  {(result.errors.preflight?.message || result.errors.bitrate?.message) &&
                    `: ${result.errors.preflight?.message || result.errors.bitrate?.message}`}
                </Typography>
              )
            }
          >
            {/* Tooltip clones its child and needs to attach a ref to it for positioning;
                InfoIcon is a plain function component that can't forward refs, so it is
                wrapped in a <span> (MUI's documented pattern for this case). */}
            <span tabIndex={0} aria-label="View network test result details">
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
